import React from 'react';

import {
  Button,
  HStack,
  Primitive,
  Text,
  VStack,
  useToastContext,
} from '@rtkwlf/fenrir-react';
import { faInfoCircle } from '@rtkwlf/pro-solid-svg-icons/faInfoCircle';
import { type Attachment } from '@rtkwlf/track-public';
import prettyBytes from 'pretty-bytes';

import { useGetOrganizationSpidrAttachmentLinkMutation } from '@apis/track-public/query-options';
import { EmptyResults } from '@packages/components/empty-results/empty-results';

import { useSpidrIDParam } from '../hooks/use-spidr-id-param';
import { useSecurityJourneyTranslations } from '../locales/use-security-journey-translations';

import styles from './spidr-details.module.css';

type AttachmentEntryProps = {
  attachment: Attachment;
};

const AttachmentEntry: React.FC<AttachmentEntryProps> = ({ attachment }) => {
  const { t } = useSecurityJourneyTranslations();

  const { addToast } = useToastContext();

  const { spidrID } = useSpidrIDParam();

  const { mutate: getAttachmentLink } =
    useGetOrganizationSpidrAttachmentLinkMutation();

  const getLink = React.useCallback(() => {
    getAttachmentLink(
      {
        spidrID,
        attachmentID: attachment.uuid,
      },
      {
        onSuccess: (data) => {
          window.open(data.linkUrl, '_blank');
        },
        onError: (err) => {
          addToast({
            title: t('spidrID.attachments.errorTitle'),
            message: t('spidrID.attachments.errorMessage', {
              message: err.message,
            }),
            appearance: 'danger',
          });
        },
      },
    );
  }, [t, getAttachmentLink, spidrID, attachment.uuid, addToast]);

  return (
    <HStack
      className={styles['markdown-background']}
      data-testid={`attachment-${attachment.uuid}`}
      padding="medium"
      width="full"
    >
      <HStack gap="xsmall">
        <Text styledAs="body2">{attachment.fileName}</Text>{' '}
        <Text styledAs="body1">
          ({prettyBytes(attachment.fileSize).replace(' ', '')})
        </Text>
      </HStack>
      <Button
        marginLeft="auto"
        marginRight="small"
        variant="link"
        onClick={() => getLink()}
      >
        {t('spidrID.attachments.downloadFile')}
      </Button>
    </HStack>
  );
};

type AttachmentsListProps = {
  attachments: Attachment[];
};

export const AttachmentsList: React.FC<AttachmentsListProps> = ({
  attachments,
}) => {
  const { t } = useSecurityJourneyTranslations();

  return attachments.length === 0 ? (
    <EmptyResults
      variant="banner"
      icon={faInfoCircle}
      title={t('spidrID.attachments.noAttachmentsMessage.title')}
      description={t('spidrID.attachments.noAttachmentsMessage.description')}
    />
  ) : (
    <Primitive.div width="full">
      <VStack>
        {attachments.map((a) => (
          <AttachmentEntry attachment={a} key={a.uuid} />
        ))}
      </VStack>
    </Primitive.div>
  );
};
